<script lang="ts">
	import Dialog from '#lib/components/Dialog.svelte';
	import Toast from '#lib/components/Toast.svelte';
	import BaseMap from '#lib/components/map/BaseMap.svelte';
	import DrawBox from '#lib/components/map/DrawBox.svelte';
	import { clientExport } from '#lib/components/map/client-export.js';
	import { EXPORT_PRINT_DPI } from '#lib/components/map/export-tiles.js';
	import { formatViewHash, type MapView } from '#lib/components/map/view-hash.js';
	import { WEBSITE_NAME } from '#lib/constants.js';
	import { page } from '$app/state';
	import { MetaTags } from 'svelte-meta-tags';
	import welcomePopupContent from './welcome-popup.md';

	let isWelcomeDialogOpen = $state(false);
	let isDrawingExport = $state(false);
	let isExportWarningPopupOpen = $state(false);
	let isExportAreaTooBigPopupOpen = $state(false);
	let isExportLoading = $state(false);
	let isShareFallbackPopupOpen = $state(false);
	let shareUrl = $state('');
	let toastMessage: string | undefined = $state();
	let currentView: MapView | undefined;

	async function shareView() {
		if (currentView === undefined) return;
		shareUrl = new URL(page.url.pathname + formatViewHash(currentView), page.url.origin).href;

		if (matchMedia('(pointer: coarse)').matches && navigator.share !== undefined) {
			try {
				await navigator.share({ title: WEBSITE_NAME, url: shareUrl });
				return;
			} catch (error) {
				// The user closed the share sheet
				if (error instanceof DOMException && error.name === 'AbortError') return;
			}
		}

		try {
			await navigator.clipboard.writeText(shareUrl);
			toastMessage = 'Lien de la vue copié dans le presse-papier';
		} catch {
			isShareFallbackPopupOpen = true;
		}
	}
</script>

<MetaTags
	title={WEBSITE_NAME}
	description="Une carte de course d'orientation de la France entière (en cours de création)"
	canonical={new URL(page.url.pathname, page.url.origin).href}
	openGraph={{
		url: new URL(page.url.pathname, page.url.origin).href,
		locale: 'fr_FR',
		images: [
			{ url: '/facebook.png', width: 1200, height: 630, alt: 'A LiDAR generated orienteering map' },
			{ url: '/whatsapp.png', width: 400, height: 400, alt: 'A LiDAR generated orienteering map' }
		]
	}}
	twitter={{ cardType: 'summary_large_image' }}
/>

<Dialog bind:open={isWelcomeDialogOpen}>
	<div flex mt-4>
		<h2 text-5 font-bold grow>Le projet Mapant.fr</h2>

		<button
			type="button"
			m-0
			p-1
			border-none
			h-fit
			class="outline"
			aria-label="Fermer le popup"
			onclick={() => (isWelcomeDialogOpen = false)}
			><i i-carbon-close-large w-5 h-5 block></i></button
		>
	</div>

	{@html welcomePopupContent}

	<p text="right 5 gray-6"><em>Nicolas Rio</em></p>

	<p flex justify-end gap-4>
		<button type="button" class="outline" m-0 onclick={() => (isWelcomeDialogOpen = false)}
			>Retour à la carte</button
		>
	</p>
</Dialog>

<Dialog bind:open={isExportWarningPopupOpen}>
	<p font-bold mt-4>Vous êtes sur le point d'exporter une zone de la carte Mapant.fr</p>

	<p>
		Attention, la possession de cette carte n'implique pas un droit d'accès permanent au terrain.
		Avant de visiter la zone cartographiée, assurez vous d'avoir les droits d'accès nécessaires.
	</p>

	<p>
		Mapant.fr et Nicolas Rio déclinent toute responsabilité en cas d'accès non autorisé à une zone
		de la carte Mapant.fr.
	</p>

	<p>
		L'export généré est une image au format png. La carte doit être imprimée à la résolution de {EXPORT_PRINT_DPI}
		dpi pour être à l'échelle 1:10000. Pour imprimer la carte à la bonne échelle, vous pouvez utiliser
		l'excellent logiciel de traçage [Purple Pen](https://purple-pen.org/).
	</p>

	<p flex justify-end gap-4>
		<button type="button" m-0 class="outline" onclick={() => (isExportWarningPopupOpen = false)}
			>Annuler</button
		>

		<button
			type="button"
			m-0
			flex="!~"
			items-center
			gap-2
			w-fit
			onclick={() => {
				isExportWarningPopupOpen = false;
				isDrawingExport = true;
			}}
		>
			Exporter

			<i i-carbon-arrow-right block h-5 w-5></i>
		</button>
	</p>
</Dialog>

<Dialog bind:open={isExportAreaTooBigPopupOpen}>
	<p font-bold mt-4>La zone est trop grande pour être exportée.</p>

	<p flex justify-end gap-4>
		<button type="button" m-0 class="outline" onclick={() => (isExportAreaTooBigPopupOpen = false)}
			>Fermer</button
		>
	</p>
</Dialog>

<Dialog bind:open={isShareFallbackPopupOpen}>
	<p font-bold mt-4>Impossible de copier le lien automatiquement</p>

	<p>Copiez ce lien pour partager la vue actuelle de la carte :</p>

	<input type="text" readonly value={shareUrl} onfocus={(e) => e.currentTarget.select()} />

	<p flex justify-end gap-4>
		<button type="button" m-0 class="outline" onclick={() => (isShareFallbackPopupOpen = false)}
			>Fermer</button
		>
	</p>
</Dialog>

{#snippet beforeSources()}
	<button
		onclick={() => (isWelcomeDialogOpen = true)}
		type="button"
		relative
		m-0
		p="x-2 y-1"
		bg-white
		class="outline"
	>
		Infos
	</button>
{/snippet}

{#snippet afterSources()}
	<a href="/guesser" role="button" m-0 p="x-2 y-1" bg-white class="outline map-guesser-link">
		<i i-carbon-location block w-4 h-4 aria-hidden="true"></i>
		<span>Guesser</span>
		<span class="guesser-badge">Nouveau</span>
	</a>
{/snippet}

<BaseMap
	persistMapState
	readViewHash
	onViewChange={({ zoom, center, rotation }) => (currentView = { zoom, center, rotation })}
	{beforeSources}
	{afterSources}
	class={isDrawingExport ? 'cursor-crosshair' : undefined}
>
	<button
		absolute
		top-12
		right-2
		flex
		items-center
		justify-center
		w-8
		h-8
		p-0
		bg-white
		class="outline"
		type="button"
		aria-label="Draw"
		disabled={isExportLoading}
		onclick={() => {
			if (isDrawingExport) isDrawingExport = false;
			else isExportWarningPopupOpen = true;
		}}
	>
		{#if isExportLoading}
			<i aria-busy="true" w-5 h-5 block></i>
		{:else}
			<i i-carbon-download w-5 h-5 block></i>
		{/if}
	</button>

	<button
		absolute
		top-22
		right-2
		flex
		items-center
		justify-center
		w-8
		h-8
		p-0
		bg-white
		class="outline"
		type="button"
		aria-label="Partager la vue"
		title="Partager la vue"
		onclick={shareView}
	>
		<i i-carbon-share w-5 h-5 block></i>
	</button>

	<Toast bind:message={toastMessage} class="absolute top-2 left-1/2 -translate-x-1/2 z-1" />

	{#if isDrawingExport}
		<DrawBox
			ondrawend={async (e) => {
				isDrawingExport = false;
				const extent = e.feature.getGeometry()?.getExtent();
				if (!extent) return;
				const [x1, y1, x2, y2] = extent;
				isExportLoading = true;
				const error = await clientExport({ x1, y1, x2, y2 });
				isExportLoading = false;
				if (error !== null) isExportAreaTooBigPopupOpen = true;
			}}
		/>
	{/if}
</BaseMap>

<style>
	.map-guesser-link {
		display: inline-flex;
		align-items: center;
		gap: 0.4rem;
		white-space: nowrap;
	}

	.guesser-badge {
		border-radius: 0.25rem;
		background: var(--pico-primary-background);
		color: white;
		padding: 0.1rem 0.3rem;
		font-size: 0.625rem;
		font-weight: 700;
		line-height: 1.4;
	}
</style>
